import React, { FC, useContext } from 'react';
import styled from 'styled-components';

import ProgressCaret from '../../../assets/icons/progress-bar-caret.svg';
import { CreditCardApplicationContext } from '../../../CreditCardApplicationContext';
import {
  CARD_BREAKPOINT,
  MOBILE_BREAKPOINT,
  TABLET_BREAKPOINT,
  SMALL_MOBILE_BREAKPOINT,
  colors,
} from '../../constants';

// Nav Progress Bar

// Interface --------------------------------------------------------------------------------------
interface NavProgressBarProps {
  progressState: string;
}

interface StyledProps {
  focus?: boolean;
  conditionalOnCashflowVerification?: boolean;
}

// Styles -----------------------------------------------------------------------------------------
const Container = styled.div`
  width: 100%;
  padding: 0 0 0.25rem;
  display: flex;
  justify-content: center;
  align-items: center;
`;
Container.displayName = 'Container';

const ProgressContent = styled.div<StyledProps>`
  width: 100%;
  max-width: 40rem;
  padding: 0 6.6875rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  ${(p: StyledProps) =>
    !p.conditionalOnCashflowVerification &&
    `
    padding: 0 10.6875rem;
  `};
  @media (max-width: ${TABLET_BREAKPOINT}) {
    padding: 0;
    ${(p: StyledProps) =>
      !p.conditionalOnCashflowVerification &&
      `
      padding: 0 3.5rem;
    `};
  }
  @media (max-width: ${CARD_BREAKPOINT}) {
    max-width: unset;
  }
  @media (max-width: ${SMALL_MOBILE_BREAKPOINT}) {
    padding: 0;
  }
`;
ProgressContent.displayName = 'ProgressContent';

const CaretRight = styled(ProgressCaret)`
  margin: 0.125rem 0 0;
  @media (max-width: ${CARD_BREAKPOINT}) {
    width: 0.25rem;
  }
`;
CaretRight.displayName = 'CaretRight';

const ProgressState = styled.p<StyledProps>`
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  color: ${colors.TEXT_LABEL};
  ${(p: StyledProps) =>
    p.focus === true &&
    `
    font-weight: 500;
    color: ${colors.TEXT_PRIMARY};
  `};
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    font-size: 0.75rem;
  }
`;
ProgressState.displayName = 'ProgressState';

const ProgressIndicator = styled.svg<StyledProps>`
  height: 0.3125rem;
  width: 0.3125rem;
  margin: 0 0.5rem 0 0;
  circle {
    fill: transparent;
  }
  ${(p: StyledProps) =>
    p.focus === true &&
    `
    circle {
      fill: ${colors.UI_PRIMARY_ACCENT};
    }
  `};
  ${(p: StyledProps) =>
    p.focus === false &&
    `
    display: none;
  `};
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    margin: 0 0.4375rem 0 0;
  }
`;
ProgressIndicator.displayName = 'ProgressIndicator';

// Component --------------------------------------------------------------------------------------
export const NavProgressBar: FC<NavProgressBarProps> = ({ progressState }) => {
  const { creditCardApplication } = useContext(CreditCardApplicationContext);
  const { offerCheck } = creditCardApplication || {};
  const { offer } = offerCheck || {};
  const { conditionalOnCashflowVerification } = offer || {};

  // Do not show the ProgressBar for non-funnel pages or the Apply page
  if (progressState === 'APPLY' || progressState === 'NON-FUNNEL PAGE') {
    return null;
  }

  return (
    <Container id="progress-bar">
      <ProgressContent
        conditionalOnCashflowVerification={conditionalOnCashflowVerification}
      >
        <ProgressState id="apply">Apply</ProgressState>
        <CaretRight />
        <ProgressState
          id="review-offer"
          focus={progressState === 'REVIEW OFFER'}
        >
          <ProgressIndicator
            id="review-offer-progression-indicator"
            focus={progressState === 'REVIEW OFFER'}
          >
            <circle r={2.5} cx={2.5} cy={2.5} />
          </ProgressIndicator>
          Review offer
        </ProgressState>
        <CaretRight />
        {conditionalOnCashflowVerification && (
          <>
            <ProgressState
              id="connect-bank"
              focus={progressState === 'CONNECT BANK'}
            >
              <ProgressIndicator
                id="connect-bank-progression-indicator"
                focus={progressState === 'CONNECT BANK'}
              >
                <circle r={2.5} cx={2.5} cy={2.5} />
              </ProgressIndicator>
              Connect bank
            </ProgressState>
            <CaretRight />
          </>
        )}
        <ProgressState id="finish-up" focus={progressState === 'FINISH UP'}>
          <ProgressIndicator
            id="finish-up-progression-indicator"
            focus={progressState === 'FINISH UP'}
          >
            <circle r={2.5} cx={2.5} cy={2.5} />
          </ProgressIndicator>
          Finish up
        </ProgressState>
      </ProgressContent>
    </Container>
  );
};
